/**
 * Authentication service for AR app.
 *
 * Handles:
 * - User login/logout
 * - Token storage and validation
 * - User session management
 */

const AUTH_TOKEN_KEY = 'ar_auth_token';
const USER_KEY = 'ar_user';

class AuthService {
  constructor() {
    this.token = null;
    this.user = null;
    this.initialized = false;
  }

  /**
   * Initialize auth service from stored credentials.
   */
  initialize() {
    const storedToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      this.token = storedToken;
      try {
        this.user = JSON.parse(storedUser);
      } catch (e) {
        this.user = null;
      }
    }

    this.initialized = true;
    return this.isAuthenticated();
  }

  /**
   * Login with username and password.
   */
  async login(username, password) {
    const response = await fetch('/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // Store credentials
    this.token = data.token;
    this.user = {
      username: data.username,
      displayName: data.display_name
    };

    sessionStorage.setItem(AUTH_TOKEN_KEY, this.token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(this.user));

    return data;
  }

  /**
   * Logout current user.
   */
  async logout() {
    try {
      if (this.token) {
        await fetch('/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          }
        });
      }
    } catch (err) {
      console.warn('Logout request failed:', err);
    } finally {
      this.clearCredentials();
    }
  }

  /**
   * Clear stored credentials.
   */
  clearCredentials() {
    this.token = null;
    this.user = null;
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  }

  /**
   * Check if user is authenticated.
   */
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  /**
   * Get current auth token.
   */
  getToken() {
    return this.token;
  }

  /**
   * Get current user.
   */
  getUser() {
    return this.user;
  }

  /**
   * Get display name for current user.
   */
  getDisplayName() {
    return this.user?.displayName || this.user?.username || 'User';
  }
}

// Singleton instance
const authService = new AuthService();

export default authService;
